"use client"

import React from "react"
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react"
import { setUserLanguage } from "./api"
import { languageCodeToFullName } from "./language-utils"
import { getJWTToken } from "./api"

// Define supported languages
export type Language = "en" | "de" | "fr" | "fa" | "es"

// Define the context shape
interface I18nContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string, params?: Record<string, string>) => string
  tArray: (key: string) => string[] | null
  loadTranslations: (namespace: string) => Promise<void>
  isRtl: boolean
  isLoading: boolean
}

// Create the context with default values
const I18nContext = createContext<I18nContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key) => key,
  tArray: () => null,
  loadTranslations: async () => {},
  isRtl: false,
  isLoading: false,
})

// RTL languages
const rtlLanguages: Language[] = ["fa"]

// Default translations for common namespace to avoid 404 errors
const defaultCommonTranslations = {
  en: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
  },
  de: {
    loading: "Wird geladen...",
    error: "Fehler",
    success: "Erfolg",
    cancel: "Abbrechen",
    save: "Speichern",
    delete: "Löschen",
    edit: "Bearbeiten",
    close: "Schließen",
  },
  fr: {
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    edit: "Modifier",
    close: "Fermer",
  },
  fa: {
    loading: "در حال بارگذاری...",
    error: "خطا",
    success: "موفقیت",
    cancel: "لغو",
    save: "ذخیره",
    delete: "حذف",
    edit: "ویرایش",
    close: "بستن",
  },
  es: {
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    close: "Cerrar",
  },
}

// Provider component
export function I18nProvider({ children }: { children: ReactNode }) {
  // Initialize language from localStorage or default to browser language.
  // German is the product default (DE-first market) — used until a saved
  // preference, browser language, or IP detection overrides it.
  const [language, setLanguageState] = useState<Language>("de")
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({
    // Initialize with default common translations
    common:
      defaultCommonTranslations[language as keyof typeof defaultCommonTranslations] || defaultCommonTranslations.en,
  })
  const [loadedNamespaces, setLoadedNamespaces] = useState<string[]>(["common"])
  const [isLoading, setIsLoading] = useState(false)
  const isRtl = rtlLanguages.includes(language)
  // Parallel English store. When the current language is not English, every namespace
  // load also (best-effort, once per namespace) fetches the English copy of that same
  // namespace into this store. t()/tArray() consult it when a key is missing from the
  // current-language file, so an individual missing KEY degrades to English the same
  // way loadTranslationsFile already degrades a missing FILE to English.
  const [fallbackTranslations, setFallbackTranslations] = useState<Record<string, any>>({})
  // Track which namespaces are loaded for which language to prevent unnecessary reloads
  const loadedNamespacesByLanguageRef = useRef<Record<string, Set<string>>>({})
  // Track which namespaces already have (or already attempted) an English fallback fetch,
  // so it happens at most once per namespace rather than once per t() call or per reload.
  const loadedFallbackNamespacesRef = useRef<Set<string>>(new Set())
  // Keep a ref to current language to use in stable callbacks
  const languageRef = useRef<Language>(language)

  // Initialize language from localStorage on client side
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem("language") as Language
      if (savedLanguage && ["en", "de", "fr", "fa", "es"].includes(savedLanguage)) {
        setLanguageState(savedLanguage)
      }
      // No saved preference → keep the German default ("de"). We deliberately do
      // NOT auto-switch to navigator.language / IP geolocation here: this is a
      // DE-first product and the landing page must always render in German until
      // the user explicitly picks another language (which is then persisted to
      // localStorage above) or a logged-in user's backend pref_lang applies.
    } catch (error) {
      console.error("Error accessing localStorage:", error)
    }
  }, [])

  // Listen for language changes from auth context (when pref_lang is set)
  useEffect(() => {
    const handleLanguageChanged = (event: CustomEvent) => {
      const newLanguage = event.detail?.language as Language
      if (newLanguage && ["en", "de", "fr", "fa", "es"].includes(newLanguage)) {
        setLanguageState(newLanguage)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("language-changed", handleLanguageChanged as EventListener)
      return () => {
        window.removeEventListener("language-changed", handleLanguageChanged as EventListener)
      }
    }
  }, [])

  // Update language ref when language changes
  useEffect(() => {
    languageRef.current = language
  }, [language])

  // Shared fetch/parse/content-type-check for a single namespace file, used by every
  // loader below (current-language load, its whole-file English fallback, and the
  // per-key English fallback store) so there is exactly one place that knows how to
  // fetch a translations file.
  const fetchNamespaceFile = useCallback(async (lang: Language, namespace: string): Promise<any> => {
    const response = await fetch(`/translations/${lang}/${namespace}.json`, {
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to load translations for ${lang}/${namespace} (Status: ${response.status})`)
    }

    const contentType = response.headers.get("content-type")
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error(`Invalid content type: ${contentType}. Expected application/json`)
    }

    return response.json()
  }, [])

  // Populate (once) the English fallback store for a namespace. No-op if already
  // loaded/attempted, or for "common" (which already has built-in translations).
  // Never throws - a failed fallback fetch is logged and simply leaves the store
  // empty for this namespace, so t()/tArray() fall through to their final resort.
  const loadEnglishFallback = useCallback(
    async (namespace: string) => {
      if (namespace === "common") {
        return
      }
      if (loadedFallbackNamespacesRef.current.has(namespace)) {
        return
      }
      // Mark eagerly so concurrent calls for the same namespace don't double-fetch.
      loadedFallbackNamespacesRef.current.add(namespace)
      try {
        const data = await fetchNamespaceFile("en", namespace)
        setFallbackTranslations((prev) => ({
          ...prev,
          [namespace]: data,
        }))
      } catch (error) {
        console.error(`Error loading English fallback translations for ${namespace}:`, error)
        // Allow a later load to retry instead of permanently giving up.
        loadedFallbackNamespacesRef.current.delete(namespace)
      }
    },
    [fetchNamespaceFile],
  )

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRtl ? "rtl" : "ltr"
    document.documentElement.lang = language

    // Apply Vazirmatn font for Persian language
    if (language === "fa") {
      document.documentElement.classList.add("font-vazirmatn")
    } else {
      document.documentElement.classList.remove("font-vazirmatn")
    }

    // Update common translations when language changes
    setTranslations((prev) => ({
      ...prev,
      common:
        defaultCommonTranslations[language as keyof typeof defaultCommonTranslations] || defaultCommonTranslations.en,
    }))

    // Reload all previously loaded namespaces when language changes
    // Always reload to ensure translations are updated for the new language
    const reloadNamespaces = async () => {
      setIsLoading(true)
      try {
        for (const namespace of loadedNamespaces) {
          if (namespace !== "common") {
            // Skip common as we've already set it
            // Always reload to ensure we have the correct translations for current language
            // Load translations directly for the new language (not using loadTranslationsFile which uses ref)
            try {
              const data = await fetchNamespaceFile(language, namespace)
              setTranslations((prev) => ({
                ...prev,
                [namespace]: data,
              }))

              // Mark as loaded for this language
              if (!loadedNamespacesByLanguageRef.current[language]) {
                loadedNamespacesByLanguageRef.current[language] = new Set()
              }
              loadedNamespacesByLanguageRef.current[language].add(namespace)

              // Keep the English fallback store filled for this namespace so t()/tArray()
              // can fall back to it for individual keys missing from this language's file.
              if (language !== "en") {
                void loadEnglishFallback(namespace)
              }
            } catch (error) {
              console.error(`Error loading translations for ${language}/${namespace}:`, error)
              // Try English fallback
              if (language !== "en") {
                try {
                  const fallbackData = await fetchNamespaceFile("en", namespace)
                  setTranslations((prev) => ({
                    ...prev,
                    [namespace]: fallbackData,
                  }))
                  // The main store now holds English data for this namespace - cache it
                  // in the fallback store too so loadEnglishFallback doesn't refetch it.
                  setFallbackTranslations((prev) => ({
                    ...prev,
                    [namespace]: fallbackData,
                  }))
                  loadedFallbackNamespacesRef.current.add(namespace)
                } catch (fallbackError) {
                  console.error(`Error loading fallback translations:`, fallbackError)
                }
              }
            }
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    if (loadedNamespaces.length > 0) {
      reloadNamespaces()
    }
  }, [language, isRtl])

  // Set language and save to localStorage
  const setLanguage = useCallback((lang: Language) => {
    try {
      setLanguageState(lang)
      localStorage.setItem("language", lang)
      
      // If user is logged in, sync language with backend
      if (typeof window !== "undefined") {
        const token = getJWTToken()
        if (token) {
          try {
            // Extract user_id from JWT token
            const tokenParts = token.split(".")
            if (tokenParts.length > 1) {
              const payload = JSON.parse(atob(tokenParts[1]))
              const userId = payload.sub || payload.user_id || payload.id
              
              if (userId) {
                // Convert language code to full name for backend
                const fullLanguageName = languageCodeToFullName(lang)
                
                // Call API to update language preference (fire and forget)
                setUserLanguage(fullLanguageName, userId).catch((error) => {
                  console.error("Failed to sync language with backend:", error)
                  // Don't block language change if API call fails
                })
              }
            }
          } catch (error) {
            console.error("Error extracting user_id from token:", error)
            // Don't block language change if token parsing fails
          }
        }
      }
    } catch (error) {
      console.error("Error setting language in localStorage:", error)
    }
  }, [])

  // Load translations from JSON file
  // This function uses languageRef to get current language without creating dependency
  const loadTranslationsFile = useCallback(
    async (namespace: string) => {
      // Skip loading for common namespace as we have default translations
      if (namespace === "common") {
        return
      }

      setIsLoading(true)
      try {
        // Get current language from ref (stable, no dependency)
        const currentLanguage = languageRef.current
        const data = await fetchNamespaceFile(currentLanguage, namespace)

        setTranslations((prev) => ({
          ...prev,
          [namespace]: data,
        }))

        // File loaded fine, but it may still be missing individual keys that exist
        // in English. Populate the English fallback store (once per namespace) so
        // t()/tArray() can fall back to it for those keys. Skipped entirely when the
        // current language IS English - no extra fetch, no extra work.
        if (currentLanguage !== "en") {
          void loadEnglishFallback(namespace)
        }
      } catch (error) {
        const currentLanguage = languageRef.current
        console.error(`Error loading translations for ${currentLanguage}/${namespace}:`, error)

        // Try to load English as fallback if not already using English
        if (currentLanguage !== "en") {
          try {
            const fallbackData = await fetchNamespaceFile("en", namespace)

            setTranslations((prev) => ({
              ...prev,
              [namespace]: fallbackData,
            }))
            // The main store now holds English data for this namespace - cache it in
            // the fallback store too so loadEnglishFallback doesn't redundantly refetch it.
            setFallbackTranslations((prev) => ({
              ...prev,
              [namespace]: fallbackData,
            }))
            loadedFallbackNamespacesRef.current.add(namespace)
          } catch (fallbackError) {
            console.error(`Error loading fallback translations:`, fallbackError)

            // Use an empty object as a last resort to prevent further errors
            setTranslations((prev) => ({
              ...prev,
              [namespace]: {},
            }))
          }
        } else {
          // Use an empty object as a last resort to prevent further errors
          setTranslations((prev) => ({
            ...prev,
            [namespace]: {},
          }))
        }
      } finally {
        setIsLoading(false)
      }
    },
    [fetchNamespaceFile, loadEnglishFallback],
  )

  // Load translations for a namespace
  // This function is stable and doesn't change when language changes
  // It uses languageRef to get current language without creating dependency
  const loadTranslations = useCallback(
    async (namespace: string) => {
      // If it's the common namespace, we already have default translations
      if (namespace === "common") {
        if (!loadedNamespaces.includes("common")) {
          setLoadedNamespaces((prev) => [...prev, "common"])
        }
        return
      }

      // Get current language from ref (stable, no dependency)
      const currentLanguage = languageRef.current
      const currentLangNamespaces = loadedNamespacesByLanguageRef.current[currentLanguage] || new Set()
      
      // Check if already loaded for current language
      if (currentLangNamespaces.has(namespace)) {
        return
      }

      // Skip if already in loadedNamespaces but not for this language (will be reloaded by language change effect)
      if (loadedNamespaces.includes(namespace) && !currentLangNamespaces.has(namespace)) {
        // Will be handled by language change effect
        return
      }

      setLoadedNamespaces((prev) => {
        if (!prev.includes(namespace)) {
          return [...prev, namespace]
        }
        return prev
      })
      
      await loadTranslationsFile(namespace)
      
      // Mark as loaded for current language
      if (!loadedNamespacesByLanguageRef.current[currentLanguage]) {
        loadedNamespacesByLanguageRef.current[currentLanguage] = new Set()
      }
      loadedNamespacesByLanguageRef.current[currentLanguage].add(namespace)
    },
    [loadTranslationsFile], // Function is stable - doesn't change when language changes
  )

  // Helper function to get nested object value by dot notation
  const getNestedValue = (obj: any, path: string): string | undefined => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  // Translation function
  const t = useCallback(
    (key: string, params?: Record<string, string>) => {
      // Split key by dot notation (e.g., "header.login")
      const parts = key.split(".")

      // If key has namespace prefix (e.g., "header.login")
      if (parts.length > 1 && loadedNamespaces.includes(parts[0])) {
        const namespace = parts[0]
        const translationKey = parts.slice(1).join(".")

        // Get translation from namespace using nested object traversal
        let translation = getNestedValue(translations[namespace], translationKey)

        if (!translation || typeof translation !== 'string') {
          // Missing from the current language's (successfully loaded) file - fall back
          // to the English copy of this namespace before giving up on the key.
          const fallbackTranslation = getNestedValue(fallbackTranslations[namespace], translationKey)
          if (fallbackTranslation && typeof fallbackTranslation === 'string') {
            translation = fallbackTranslation
          } else {
            return key // Return key if translation not found in current language or English fallback
          }
        }

        // Replace parameters
        if (params) {
          Object.entries(params).forEach(([param, value]) => {
            translation = translation?.replace(new RegExp(`{{${param}}}`, "g"), value) || translation
          })
        }

        return translation
      }

      // If key doesn't have namespace prefix, search in all loaded namespaces
      for (const namespace of loadedNamespaces) {
        const translation = getNestedValue(translations[namespace], key)
        
        if (translation && typeof translation === 'string') {
          // Replace parameters
          if (params) {
            let finalTranslation = translation
            Object.entries(params).forEach(([param, value]) => {
              finalTranslation = finalTranslation.replace(new RegExp(`{{${param}}}`, "g"), value)
            })
            return finalTranslation
          }

          return translation
        }
      }

      // Not found in the current language's data for any loaded namespace - try the
      // English fallback store for each loaded namespace before giving up.
      for (const namespace of loadedNamespaces) {
        const fallbackTranslation = getNestedValue(fallbackTranslations[namespace], key)

        if (fallbackTranslation && typeof fallbackTranslation === 'string') {
          if (params) {
            let finalTranslation = fallbackTranslation
            Object.entries(params).forEach(([param, value]) => {
              finalTranslation = finalTranslation.replace(new RegExp(`{{${param}}}`, "g"), value)
            })
            return finalTranslation
          }

          return fallbackTranslation
        }
      }

      return key // Return key if translation not found
    },
    [translations, fallbackTranslations, loadedNamespaces],
  )

  // Translation function for arrays
  const tArray = useCallback(
    (key: string): string[] | null => {
      const parts = key.split(".")

      // If key has namespace prefix (e.g., "onboarding_wizard.email_help")
      if (parts.length > 1 && loadedNamespaces.includes(parts[0])) {
        const namespace = parts[0]
        const translationKey = parts.slice(1).join(".")
        const translation = getNestedValue(translations[namespace], translationKey)

        if (Array.isArray(translation)) {
          return translation
        }

        // Missing (or not an array) in the current language's file - fall back to the
        // English copy of this namespace before giving up.
        const fallbackTranslation = getNestedValue(fallbackTranslations[namespace], translationKey)
        if (Array.isArray(fallbackTranslation)) {
          return fallbackTranslation
        }

        return null
      }

      // If key doesn't have namespace prefix, search in all loaded namespaces
      for (const namespace of loadedNamespaces) {
        const translation = getNestedValue(translations[namespace], key)
        if (Array.isArray(translation)) {
          return translation
        }
      }

      // Not found in current-language data for any loaded namespace - try the English
      // fallback store for each loaded namespace before giving up.
      for (const namespace of loadedNamespaces) {
        const fallbackTranslation = getNestedValue(fallbackTranslations[namespace], key)
        if (Array.isArray(fallbackTranslation)) {
          return fallbackTranslation
        }
      }

      return null
    },
    [translations, fallbackTranslations, loadedNamespaces],
  )

  // Replace JSX with React.createElement
  return React.createElement(
    I18nContext.Provider,
    { value: { language, setLanguage, t, tArray, loadTranslations, isRtl, isLoading } },
    children,
  )
}

// Hook to use the i18n context - renamed back to useI18n to match existing imports
export function useI18n() {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error("useI18n must be used within an I18nProvider")
  }
  return context
}

// For backward compatibility, also export as useTranslation
export const useTranslation = useI18n

// Export supported languages
export const languages: Language[] = ["en", "de", "fr", "es", "fa"]
